import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  CustomButtonLinkScope,
  CustomButtonLinkTarget
} from "types/landlord/address";
import { getTargetName, targetLabel, type PropertyDictionary } from "./helpers";

const TargetRows = ({
  targets,
  scope,
  noteText,
  noteClassName,
  dictionary
}: {
  targets: CustomButtonLinkTarget[];
  scope: CustomButtonLinkScope;
  noteText: string;
  noteClassName: string;
  dictionary: PropertyDictionary;
}) => (
  <>
    {targets.map((target) => (
      <div
        className="flex items-center justify-between gap-4 py-2"
        key={target.id}
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-content-700">
            {getTargetName(target, scope, dictionary)}
          </p>
          <p className="truncate text-sm text-content-300">
            {target.address || "No address"}
          </p>
        </div>
        <p className={`shrink-0 text-xs font-bold ${noteClassName}`}>
          {noteText}
        </p>
      </div>
    ))}
  </>
);

export const OverwriteCustomButtonLinkDialog = ({
  opened,
  onClose,
  onConfirm,
  scope,
  sourceName,
  selectedCount,
  overwriteTargets,
  pmsSyncedTargets,
  isAllMode,
  allModeOverwriteCount,
  allModePmsSyncedCount,
  isPending,
  canClose
}: {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  scope: CustomButtonLinkScope;
  sourceName: string;
  selectedCount: number;
  overwriteTargets: CustomButtonLinkTarget[];
  pmsSyncedTargets: CustomButtonLinkTarget[];
  isAllMode: boolean;
  allModeOverwriteCount: number;
  allModePmsSyncedCount: number;
  isPending: boolean;
  canClose: boolean;
}) => {
  const dictionary = useUnitsPropertiesDictionary();

  const overwriteCount = isAllMode
    ? allModeOverwriteCount
    : overwriteTargets.length;
  const pmsSyncedCount = isAllMode
    ? allModePmsSyncedCount
    : pmsSyncedTargets.length;
  const countPrefix = isAllMode ? "up to " : "";

  const summaryParts: string[] = [];
  if (overwriteCount > 0)
    summaryParts.push(
      `${countPrefix}${overwriteCount} ${targetLabel(overwriteCount, scope, dictionary)} will have their existing link replaced`
    );
  if (pmsSyncedCount > 0)
    summaryParts.push(
      `${countPrefix}${pmsSyncedCount} ${targetLabel(pmsSyncedCount, scope, dictionary)} currently synced from the PMS will be switched to a manually-entered link`
    );

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      panelClassName="max-w-[720px] w-full"
    >
      <header className="flex items-start justify-between gap-4 border-b border-alpha-40 px-6 py-6">
        <h3 className="text-xl font-bold text-content-1000">
          Overwrite existing links?
        </h3>
        <IconButton
          HugeIcon={Cancel01Icon}
          disabled={!canClose}
          onClick={onClose}
          size={IconButtonSize.BASE}
          variant={IconButtonVariant.TEXT_BASE}
        />
      </header>

      <div className="border-b border-alpha-40 px-6 py-4">
        <p className="rounded-lg bg-warning-50 p-4 text-sm text-warning-800">
          {isAllMode ? (
            <>Of the {targetLabel(2, scope, dictionary)} matching your search</>
          ) : (
            <>
              Of the {selectedCount}{" "}
              {targetLabel(selectedCount, scope, dictionary)} you selected
            </>
          )}
          : <span className="font-bold">{summaryParts.join("; ")}</span>. The
          new value will be the link saved on {sourceName}.
        </p>
      </div>

      {!isAllMode ? (
        <div className="max-h-[240px] overflow-y-auto border-b border-alpha-40 px-6 py-4">
          <TargetRows
            dictionary={dictionary}
            noteClassName="text-warning-700"
            noteText="Current link will be replaced"
            scope={scope}
            targets={overwriteTargets}
          />
          <TargetRows
            dictionary={dictionary}
            noteClassName="text-danger-600"
            noteText="Will stop syncing from the PMS"
            scope={scope}
            targets={pmsSyncedTargets}
          />
        </div>
      ) : null}

      <footer className="flex items-center justify-end gap-2 px-6 py-4">
        <Button disabled={!canClose} onClick={onClose}>
          Cancel
        </Button>
        <Button
          loading={isPending}
          onClick={onConfirm}
          variant={ButtonVariant.PRIMARY}
        >
          Overwrite and apply
        </Button>
      </footer>
    </Modal>
  );
};
